/**
 * Referências de preços do Mercado Livre (/suggestions) — leitura do payload
 * para o resumo do Explorador de Endpoints.
 *
 * Fonte: https://developers.mercadolivre.com.br/pt_br/referencias-de-precos
 * (atualizada em 17/12/2025). Os significados de status abaixo são o texto da
 * doc; status que a doc não lista aparece cru, sem significado inventado.
 */

import { FiniteMlNumber, MlMoneyAmount, MlScalarText } from './mlPayloadValues';

/** `tone` decide a cor do selo: preço acima da referência é o caso que pede ação. */
const STATUS_MEANINGS = {
    with_benchmark_highest: {
        label: 'Acima da referência e dos concorrentes',
        meaning: 'O preço atual é mais alto que o preço de referência e que o preço máximo dos concorrentes.',
        tone: 'bad',
    },
    with_benchmark_high: {
        label: 'Alto',
        meaning: 'O preço atual é alto em relação ao preço de referência.',
        tone: 'warn',
    },
    no_benchmark_ok: {
        label: 'Na referência',
        meaning: 'O preço atual é igual ao preço de referência.',
        tone: 'good',
    },
    no_benchmark_lowest: {
        label: 'Abaixo da referência',
        meaning: 'O preço atual está abaixo do preço de referência.',
        tone: 'good',
    },
    not_optin_applied: {
        label: 'Promoção não aplicada',
        meaning: 'A referência vem de promoção (markdown) e a promoção não foi aplicada.',
        tone: 'promo',
    },
    promotion_scheduled: {
        label: 'Promoção agendada',
        meaning: 'A promoção foi aceita (opt-in), mas ainda não chegou a data da promoção.',
        tone: 'promo',
    },
    promotion_active: {
        label: 'Promoção ativa',
        meaning: 'A promoção foi aceita (opt-in) e está dentro das datas em que se aplica.',
        tone: 'promo',
    },
};

/** Motivos de estoque "unhealthy" que a doc mostra no exemplo. */
const UNHEALTHY_REASONS = { no_sales: 'sem vendas' };

/** Quantos concorrentes de `metadata.graph` o resumo lista. */
const COMPETITORS_PREVIEW = 5;

/** Status da referência com o significado da doc em português. */
export function DescribePriceSuggestionStatus(rawStatus) {
    // A doc diz que status é texto; se vier objeto, não vira "[object Object]" nem quebra a tela.
    const status = MlScalarText(rawStatus);
    const known = Object.hasOwn(STATUS_MEANINGS, status ?? '') ? STATUS_MEANINGS[status] : null;
    if (known) {
        return { status, ...known };
    }
    return {
        status,
        label: status || 'Sem status',
        meaning: 'Status que a documentação não descreve: confira o JSON.',
        tone: 'neutral',
    };
}

/**
 * Resumo do detalhe de UM anúncio (GET /suggestions/items/{id}/details).
 * Devolve null quando o payload não é um detalhe (ex.: o corpo do 404 "item
 * price suggestion not found").
 */
export function SummarizePriceSuggestion(payload) {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload) || !payload.status) {
        return null;
    }
    // Campos que a doc diz serem texto passam por MlScalarText: objeto inesperado vira null, não
    // "[object Object]" nem filho inválido do React (o JSON cru continua mostrando o que veio).
    const itemId = MlScalarText(payload.item_id);
    if (!itemId) {
        return null;
    }
    const unhealthyReason = MlScalarText(payload.promotion_detail?.unhealthy_reason);

    const costs = payload.costs && typeof payload.costs === 'object' ? payload.costs : null;
    const promotion =
        payload.promotion_detail && typeof payload.promotion_detail === 'object' ? payload.promotion_detail : null;
    const metadata = payload.metadata && typeof payload.metadata === 'object' ? payload.metadata : {};
    const graph = Array.isArray(metadata.graph) ? metadata.graph : [];

    return {
        itemId,
        status: DescribePriceSuggestionStatus(payload.status),
        currency: MlScalarText(payload.currency_id) || 'BRL',
        prices: [
            { key: 'current_price', label: 'Preço atual', amount: MlMoneyAmount(payload.current_price) },
            { key: 'suggested_price', label: 'Preço sugerido', amount: MlMoneyAmount(payload.suggested_price) },
            { key: 'lowest_price', label: 'Menor preço', amount: MlMoneyAmount(payload.lowest_price) },
            { key: 'internal_price', label: 'Preço interno (ML)', amount: MlMoneyAmount(payload.internal_price) },
        ],
        percentDifference: FiniteMlNumber(payload.percent_difference),
        ratio: FiniteMlNumber(payload.ratio),
        applicable: typeof payload.applicable_suggestion === 'boolean' ? payload.applicable_suggestion : null,
        costs: costs
            ? { sellingFees: FiniteMlNumber(costs.selling_fees), shippingFees: FiniteMlNumber(costs.shipping_fees) }
            : null,
        promotion: promotion
            ? {
                  // A doc chama de promotion_name na lista de campos e manda campaign_name no exemplo.
                  name: MlScalarText(promotion.campaign_name) ?? MlScalarText(promotion.promotion_name),
                  promotionId: MlScalarText(promotion.promotion_id),
                  discountPercent: FiniteMlNumber(promotion.discount_percent),
                  startDate: MlScalarText(promotion.campaign_start_date),
                  endDate: MlScalarText(promotion.campaign_end_date),
                  unhealthyReason: unhealthyReason
                      ? (Object.hasOwn(UNHEALTHY_REASONS, unhealthyReason) && UNHEALTHY_REASONS[unhealthyReason]) ||
                        unhealthyReason
                      : null,
                  daysUnhealthy: FiniteMlNumber(promotion.days_unhealthy),
              }
            : null,
        competitors: {
            compared: FiniteMlNumber(metadata.compared_values),
            total: graph.length,
            items: graph.slice(0, COMPETITORS_PREVIEW).map((entry) => ({
                title: MlScalarText(entry?.info?.title),
                soldQuantity: FiniteMlNumber(entry?.info?.sold_quantity),
                amount: MlMoneyAmount(entry?.price),
            })),
        },
        lastUpdated: MlScalarText(payload.last_updated),
    };
}

/** Lista de anúncios com referência (GET /suggestions/user/{id}/items): `{ total, items: [ids] }`. */
export function SummarizePriceSuggestionItems(payload) {
    if (!payload || typeof payload !== 'object' || !Array.isArray(payload.items)) {
        return null;
    }
    const items = payload.items.filter((id) => typeof id === 'string' && id.trim() !== '');
    return { total: FiniteMlNumber(payload.total) ?? items.length, items };
}
